/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: constants.ts (Queues Constants)
 🗓️ Created: 2025-01-05
 🧩 Description:
    Constants and default values for Queues module.

 🔧 Notes:
    - Centralized constants for better maintainability
    - Default form state values
    - Strategy options and known settings keys

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { FormState, Strategy, TimeBaseScore, AgentType, AgentStateMode, AgentFormState } from './types';

export const STRATEGY_OPTIONS: Strategy[] = [
  'ring-all',
  'longest-idle-agent',
  'round-robin',
  'top-down',
  'least-talk-time',
  'least-calls',
  'sequentially-by-agent-order',
  'sequentially-by-next-agent-order',
  'random'
];

export const TIME_BASE_SCORE_OPTIONS: TimeBaseScore[] = ['queue', 'system'];

export const AGENT_TYPE_OPTIONS: AgentType[] = ['Inbound', 'Outbound', 'Callback'];
export const AGENT_STATE_MODE_OPTIONS: AgentStateMode[] = ['auto', 'manual'];

export const DEFAULT_AGENT_FORM_STATE: AgentFormState = {
  extension: '',
  contactTimeout: '30',
  agentNoAnswerDelayTime: '0',
  rejectDelayTime: '0',
  busyDelayTime: '0',
  maxNoAnswer: '3',
  agentType: 'Inbound',
  stateMode: 'auto'
};

  // Known settings keys for Queue (only fields NOT in Queue Profile and NOT direct columns)
  // Fields that ARE in Queue Profile are removed and moved there
  // Fields that are direct columns (extension, greeting, language) are NOT in settings
export const KNOWN_SETTINGS_KEYS = new Set([
  'time_base_score_seconds',
  'max_wait_time_with_no_agent',
  'max_wait_time_with_no_agent_time_reached',
  'timeout_destination',
  'execute_on_hangup',
  'discard_abandoned_after',
  'abandoned_resume_allowed',
  'agent_connect_sound',
  'welcome_sound',
  'exit_sound',
  'service_level_seconds',
  'announce_sound',
  'announce_frequency',
  'caller_id_name_prefix',
  'caller_id_number_prefix',
  'caller_exit_key',
  'time_base_score',
  'description',
  'wrap_up_time',
  'cc_priority',
  'announce_hold_time',
  'announce_position',
  'max_wait_agents',
  'max_no_answer',
  'cc_max_wait_size',
  'agent_no_answer_delay_time',
  'announce_estimated_holdtime'
]);

export const DEFAULT_FORM_STATE: FormState = {
  // Tab 1: General
  queueName: '',
  queueSlug: '',
  extension: '',
  strategy: 'longest-idle-agent',
  recordCalls: false,
  maxWaitTime: '0',
  maxWaitTimeWithNoAgent: '300',
  maxWaitTimeWithNoAgentTimeReached: '',
  enabled: true,
  queueProfileId: '', // Reference to queue profile
  callbackEnabled: false, // Enable callback functionality
  callbackProfileId: '', // Callback Profile
  vipListIds: [], // VIP Lists
  wrapUpTime: '10', // Wrap-up Time
  queuePriority: '0', // Queue Priority
  afterAgentHangupDestinationModule: '', // After Agent Hangup Destination Module
  afterAgentHangupDestinationDestination: '', // After Agent Hangup Destination Value
  timeoutDestinationModule: '', // Final Destination Module
  timeoutDestinationDestination: '', // Final Destination Value
  serviceLevelSeconds: '0', // Service Level
  announceHoldTime: false, // Announce Hold Time
  announcePosition: false, // Announce Position
  
  // Tab 2: Announcements
  greeting: '',
  welcomeSound: '',
  exitSound: '',
  language: 'en-us',
  musicOnHold: 'local_stream://default',
  agentConnectSound: '', // Agent Announcement
  announceSound: '', // Periodic Announcement Sound
  announceFrequency: '0', // Periodic Announcement Frequency
  announceEstimatedHoldTime: false, // Announce Estimated Hold Time
  
  // Tab 3: Advanced
  timeBaseScore: 'queue',
  timeBaseScoreSeconds: '0',
  discardAbandonedAfter: '60',
  abandonedResumeAllowed: false,
  callerExitKey: '',
  maxWaitAgents: '0', // Max Wait Agents
  maxNoAnswer: '3', // Max No Answer
  queueMaxSize: '0', // Queue Max Size
  agentNoAnswerDelay: '0', // Agent No-Answer Delay
  
  // Tab 4: Caller Experience
  callerIdNamePrefix: '',
  callerIdNumberPrefix: '',
  
  // Tab 5: Tier Rules
  tierRulesApply: false,
  tierRuleWait: '0',
  tierRuleMultiplyLevel: false,
  tierRuleNoAgentNoWait: false,
  
  createdAt: null,
  updatedAt: null
};

